const ALLOWED_PATH_PREFIXES = [
  '/auth/',
  '/business-profiles',
  '/user-settings',
  '/api-keys',
  '/invites',
  // Connecting / managing Amazon accounts is onboarding + account management,
  // not "changing marketplace data" (what the gate is meant to block). A user
  // whose trial ended (soft_gate/hard_gate) must still be able to authorize and
  // wire up their Amazon Ads / Seller accounts — otherwise the gate fires the
  // "Subscription required" modal on the very first onboarding click (the POST
  // to /amazon/ads/oauth/start). Covers the whole connect flow: oauth start,
  // integrate start/finish, profile connect (PATCH), cancel, archive, SP.
  '/amazon/ads/oauth',
  '/amazon/ads/integrate',
  '/amazon/sp/integrate',
  // eBay: connect (oauth start / claim) and managing the connections (disconnect, delete).
  '/ebay/oauth',
  '/ebay/connections',
  // Shopify: claim a store handed off from Shopify admin (preview / claim) and manage the links.
  '/shopify/claims',
  '/shopify/connections',
  // Walmart: manage the connections (disconnect, delete). Connecting is done by support.
  '/walmart/connections',
  // Google: connect (oauth start / claim) and managing the connections and their resources.
  '/google/oauth',
  '/google/connections',
  // TikTok Ads: connect (oauth start / claim) and managing the connections and their advertisers.
  '/tiktok/ads/oauth',
  '/tiktok/ads/connections',
  // Pinterest: connect (oauth start / claim) and managing the connections and their ad accounts.
  '/pinterest/oauth',
  '/pinterest/connections',
  // Meta Ads: connect (oauth start / claim) and managing the connections and their ad accounts.
  '/meta/oauth',
  '/meta/connections',
  // Microsoft Advertising (Bing): connect (oauth start / claim) and managing the connections and their accounts.
  '/bing/oauth',
  '/bing/connections',
];

// Whole-instance allowlist. OLAP / MCP backends are analytics query engines —
// they use POST for read queries (request body carries the query DSL), so a
// "GET-only" rule would wrongly block dashboard data loads. Billing must be
// reachable for the user to resolve the gate.
const ALLOWED_CLIENTS = new Set(['billing', 'olap', 'mcp']);

const READ_METHODS = new Set(['get', 'head', 'options']);

// Gate states that block mutation requests. `soft_gate` = trial ended, in
// grace period (paid plan not yet active); `hard_gate` = grace ended. In both
// the user cannot change marketplace data until billing is resolved.
const BLOCKED_GATE_STATES = new Set(['soft_gate', 'hard_gate']);

let dialogOpen = false;

const showDialog = async () => {
  if (dialogOpen) return;
  dialogOpen = true;
  // Lazy import to avoid module-init circular deps with stores
  const { useBillingGateDialogStore } = await import('@/stores/billingGateDialog.js');
  useBillingGateDialogStore().open();
};

export const resetGateGuardDedup = () => {
  dialogOpen = false;
};

// Amazon-ads API is RPC-style: POST /amazon-ads/<entity>/list reads a list
// (campaigns, ad-groups, ads, targets, etc.) with filters in the request body.
// Bind the rule to that exact path shape so it doesn't accidentally allow
// unrelated paths that happen to end in `/list`.
const AMAZON_ADS_LIST_PATTERN = /^\/amazon-ads\/[\w-]+\/list$/;

const isWhitelistedByPath = (url = '') => {
  const path = url.split('?')[0];
  // Delegation settings must remain available to reduce/revoke access after a trial ends.
  if (path === '/ai-connections' || path.startsWith('/ai-connections/')) return true;
  if (ALLOWED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  if (AMAZON_ADS_LIST_PATTERN.test(path)) return true;
  return false;
};

export const gateGuard = async (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (READ_METHODS.has(method)) return config;

  if (ALLOWED_CLIENTS.has(config.__client)) return config;

  if (isWhitelistedByPath(config.url || '')) return config;

  const { useAccountStore } = await import('@/stores/account.js');
  const gateState = useAccountStore().currentBusinessProfile?.plan?.gateState;
  if (!BLOCKED_GATE_STATES.has(gateState)) return config;

  showDialog();
  const err = new Error('Request blocked by billing gate');
  err.code = 'ERR_BILLING_GATE';
  err.config = config;
  return Promise.reject(err);
};
